<template>
  <form class="w-full flex flex-col gap-4" @submit.prevent="onSubmit">

    <div class="flex flex-col gap-1 w-full">
      <div class="flex items-center justify-between mb-1">
        <label for="newPassword" class="text-sm md:text-base text-gray-700 font-normal"
          >{{ t("resetPassword.newPassword") }}
          <span class="text-red-500">*</span></label
        >
        <button
          type="button"
          class="flex items-center gap-1 text-gray-600 text-xs md:text-sm font-normal focus:outline-none hover:text-gray-800"
          @click="showNewPassword = !showNewPassword"
          tabindex="-1"
        >
          <i
            :class="showNewPassword ? 'ri-eye-off-line' : 'ri-eye-line'"
            class="text-base md:text-lg"
          ></i>
          <span>{{ t("login.showPassword") }}</span>
        </button>
      </div>
      <input
        :type="showNewPassword ? 'text' : 'password'"
        id="newPassword"
        v-model="localNewPassword"
        autocomplete="new-password"
        class="border border-gray-300 rounded px-3 py-2.5 md:py-4 text-sm md:text-base outline-none focus:border-gray-500 transition w-full"
        ref="newPasswordInput"
        required
      />
    </div>

    <div class="flex flex-col gap-1 w-full">
      <div class="flex items-center justify-between mb-1">
        <label for="confirmPassword" class="text-sm md:text-base text-gray-700 font-normal"
          >{{ t("resetPassword.confirmPassword") }}
          <span class="text-red-500">*</span></label
        >
        <button
          type="button"
          class="flex items-center gap-1 text-gray-600 text-xs md:text-sm font-normal focus:outline-none hover:text-gray-800"
          @click="showConfirmPassword = !showConfirmPassword"
          tabindex="-1"
        >
          <i
            :class="showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'"
            class="text-base md:text-lg"
          ></i>
          <span>{{ t("login.showPassword") }}</span>
        </button>
      </div>
      <input
        :type="showConfirmPassword ? 'text' : 'password'"
        id="confirmPassword"
        v-model="localConfirmPassword"
        autocomplete="new-password"
        class="border border-gray-300 rounded px-3 py-2.5 md:py-4 text-sm md:text-base outline-none focus:border-gray-500 transition w-full"
        required
      />
    </div>

    <div class="border border-gray-200 rounded px-3 py-3 md:p-4 w-full">
      <div class="text-gray-700 text-sm md:text-base font-normal mb-2.5">
        {{ t("resetPassword.checklist.title") }}
      </div>
      <ul class="flex flex-col md:gap-2">
        <li class="flex items-center gap-2 text-xs md:text-sm" :class="localNewPassword.length >= 8 ? 'text-green-600 font-normal' : 'text-gray-500'">
          <i :class="localNewPassword.length >= 8 ? 'ri-checkbox-circle-fill text-green-600' : 'ri-checkbox-blank-circle-line text-gray-400'" class="text-base md:text-lg flex-shrink-0"></i>
          <span>At least 8 characters</span>
        </li>
        <li class="flex items-center gap-2 text-xs md:text-sm" :class="/[A-Z]/.test(localNewPassword) ? 'text-green-600 font-normal' : 'text-gray-500'">
          <i :class="/[A-Z]/.test(localNewPassword) ? 'ri-checkbox-circle-fill text-green-600' : 'ri-checkbox-blank-circle-line text-gray-400'" class="text-base md:text-lg flex-shrink-0"></i>
          <span>1 uppercase letter</span>
        </li>
        <li class="flex items-center gap-2 text-xs md:text-sm" :class="/[a-z]/.test(localNewPassword) ? 'text-green-600 font-normal' : 'text-gray-500'">
          <i :class="/[a-z]/.test(localNewPassword) ? 'ri-checkbox-circle-fill text-green-600' : 'ri-checkbox-blank-circle-line text-gray-400'" class="text-base md:text-lg flex-shrink-0"></i>
          <span>1 lowercase letter</span>
        </li>
        <li class="flex items-center gap-2 text-xs md:text-sm" :class="/[0-9]/.test(localNewPassword) ? 'text-green-600 font-normal' : 'text-gray-500'">
          <i :class="/[0-9]/.test(localNewPassword) ? 'ri-checkbox-circle-fill text-green-600' : 'ri-checkbox-blank-circle-line text-gray-400'" class="text-base md:text-lg flex-shrink-0"></i>
          <span>1 number</span>
        </li>
      </ul>
    </div>

    <div v-if="localConfirmPassword && localConfirmPassword !== localNewPassword" class="text-red-500 text-xs md:text-sm -mt-2">
      {{ t('resetPassword.errors.mismatch') }}
    </div>

    <button
      type="submit"
      :disabled="!allValid"
      class="bg-[#7a2323] text-white font-semibold text-sm md:text-base h-10 md:h-11 w-full rounded transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {{ t("resetPassword.button") }}
    </button>
  </form>
</template>

<script setup>

import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  passwordRules: {
    type: Array,
    required: true,
  },
});
const emit = defineEmits(["submit"]);
const { t } = useI18n();

const localNewPassword = ref("");
const localConfirmPassword = ref("");
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const newPasswordInput = ref(null);

const ruleValid = (rule) => rule.regex.test(localNewPassword.value);
const allValid = computed(() => {
  const hasMinLength = localNewPassword.value.length >= 8;
  const hasUppercase = /[A-Z]/.test(localNewPassword.value);
  const hasLowercase = /[a-z]/.test(localNewPassword.value);
  const hasNumber = /[0-9]/.test(localNewPassword.value);
  const passwordsMatch = localNewPassword.value === localConfirmPassword.value;
  
  return hasMinLength && hasUppercase && hasLowercase && hasNumber && passwordsMatch;
});

function onSubmit() {
  emit("submit", {
    newPassword: localNewPassword.value,
    confirmPassword: localConfirmPassword.value,
    allValid: allValid.value,
  });
}

onMounted(() => {
  if (newPasswordInput.value) {
    newPasswordInput.value.focus();
  }
});
</script>

<style scoped>
.text-white {
  color: #fff;
}

.text-green-600 {
  color: #218632 !important;
}

.text-gray-500 {
  color: #6b7280;
}
button.text-gray-600{
  background-color: transparent;
}
li.gap-2{
  margin-bottom: 2px;
}
</style>